import type { DeliverablesFormSchema } from './schemas';
import type { DeliverableWholesalePriceFormSchema } from './wholesale-prices';

/**
 * Fields that may change during revenue lockdown without opening the lockdown override modal
 * or sending `lockdown_override_pin`. Extend as product allows.
 */
export const LOCKDOWN_EDITABLE_DELIVERABLE_FORM_KEYS = [
  'product_name',
] as const satisfies readonly (keyof DeliverablesFormSchema)[];

const lockdownEditableDeliverableKeySet = new Set<string>(LOCKDOWN_EDITABLE_DELIVERABLE_FORM_KEYS);

const normalizeWholesaleDate = (v: string | null | undefined) => (v ? v.replace(' 00:00:00', '') : v);

function normalizeWholesaleForCompare(items: DeliverableWholesalePriceFormSchema[]) {
  return items.map((i) => ({
    id: i.id,
    agreement_deliverable_id: i.agreement_deliverable_id,
    start_date: normalizeWholesaleDate(i.start_date),
    end_date: normalizeWholesaleDate(i.end_date),
    wholesale_price: i.wholesale_price,
    wholesale_discount: i.wholesale_discount,
  }));
}

function deliverableFieldDiffers(
  key: keyof DeliverablesFormSchema,
  baseline: DeliverablesFormSchema,
  next: DeliverablesFormSchema
): boolean {
  switch (key) {
    case 'product':
      return baseline.product?.id !== next.product?.id;
    case 'price_frequency':
      return baseline.price_frequency?.id !== next.price_frequency?.id;
    case 'deliverable_wholesale_price': {
      const b = baseline.deliverable_wholesale_price ?? [];
      const n = next.deliverable_wholesale_price ?? [];
      return JSON.stringify(normalizeWholesaleForCompare(b)) !== JSON.stringify(normalizeWholesaleForCompare(n));
    }
    case 'client_opt':
      return (
        baseline.client_opt?.name !== next.client_opt?.name || baseline.client_opt?.value !== next.client_opt?.value
      );
    default:
      return baseline[key] !== next[key];
  }
}

const ALL_DELIVERABLE_COMPARE_KEYS = [
  'id',
  'agreement_id',
  'product',
  'sku',
  'product_name',
  'quantity',
  'price',
  'price_frequency',
  'client_opt',
  'opted_in',
  'opted_out',
  'sort_order',
  'description',
  'deliverable_wholesale_price',
] as const satisfies readonly (keyof DeliverablesFormSchema)[];

/**
 * True when `next` differs from `baseline` only on keys in
 * {@link LOCKDOWN_EDITABLE_DELIVERABLE_FORM_KEYS} (e.g. description-only edit).
 */
export function deliverableDiffOnlyUsesAllowlistedKeys(
  baseline: DeliverablesFormSchema | null | undefined,
  next: DeliverablesFormSchema
): boolean {
  if (!baseline) {
    return false;
  }

  for (const key of ALL_DELIVERABLE_COMPARE_KEYS) {
    if (lockdownEditableDeliverableKeySet.has(key)) {
      continue;
    }
    if (deliverableFieldDiffers(key, baseline, next)) {
      return false;
    }
  }
  return true;
}

/**
 * True if react-hook-form dirty metadata for `tempAgreement_deliverables` includes
 * any field outside the lockdown-editable allowlist.
 */
export function dirtyTempAgreementDeliverablesHasDisallowedKeys(dirty: unknown): boolean {
  if (dirty === true) {
    return true;
  }
  if (!dirty || typeof dirty !== 'object') {
    return false;
  }
  if (!Array.isArray(dirty)) {
    return false;
  }

  for (const row of dirty) {
    if (dirtyDeliverableRowHasDisallowedKeys(row)) {
      return true;
    }
  }
  return false;
}

/**
 * True when the deliverable modal form's `dirtyFields` only marks allowlisted keys (e.g. `description`).
 * Prefer this over {@link deliverableDiffOnlyUsesAllowlistedKeys} for submit gating: wholesale price sync and
 * other effects can change `dto` vs `data` without marking fields dirty.
 */
export function deliverableFormDirtyOnlyAllowlisted(dirtyFields: unknown): boolean {
  if (dirtyFields == null || typeof dirtyFields !== 'object') {
    return false;
  }
  if (Array.isArray(dirtyFields)) {
    return false;
  }
  const keys = Object.keys(dirtyFields as object);
  if (keys.length === 0) {
    return false;
  }
  return !dirtyDeliverableRowHasDisallowedKeys(dirtyFields);
}

function dirtyDeliverableRowHasDisallowedKeys(node: unknown): boolean {
  if (node === true) {
    return true;
  }
  if (!node || typeof node !== 'object') {
    return false;
  }
  if (Array.isArray(node)) {
    return node.some((item) => dirtyDeliverableRowHasDisallowedKeys(item));
  }

  for (const [key, value] of Object.entries(node)) {
    if (lockdownEditableDeliverableKeySet.has(key)) {
      if (value === true) {
        continue;
      }
      if (value && typeof value === 'object') {
        continue;
      }
      continue;
    }
    if (value === true) {
      return true;
    }
    if (value && typeof value === 'object' && dirtyDeliverableRowHasDisallowedKeys(value)) {
      return true;
    }
  }
  return false;
}
